import { DEFAULT_SESSION_TYPE, STUDIO_ADDRESS } from "./constants.js";
import {
  BLANK_SESSION, applyTravelRule, initSections, sortSessions, studioPatch, clearLocationPatch,
  getJobDateRange, fmtDate, validateSessionAdd, defaultDateFor, quoteCaptureDate,
} from "./utils.js";

/* ═══ THE SCHEDULE: ONE READER, ONE WRITER (docs/quotes/quote-saving.md) ═══
   A quote stores its schedule in several places: scanDate, scanDateTBD, multiDay, the
   sessions list, jobLocation and isStudio. Every reader (the printed page, booking,
   holds, the builder) goes through readSchedule, and every change (hand edits, the AI
   box, a version restore) goes through writeSchedule, so the stored places can't
   disagree. Pure: quote in, patch out. */

const sectionsOf = (q) => q?.sections || initSections();

// A day from before multi-day stored no id. It gets the same id every time it is read
// (from its position), so the builder's rendered day and the writer's day are one day.
export const hydrateSessions = (q) =>
  (Array.isArray(q?.sessions) ? q.sessions : []).filter(Boolean).map((s, i) => ({ ...BLANK_SESSION(), ...s, id: s.id || `sess_legacy_${i + 1}` }));

/** What this quote's schedule is. */
export function readSchedule(q) {
  const multiDay = !!q?.multiDay;
  const tbd = !!q?.scanDateTBD;
  const days = sortSessions(hydrateSessions(q));
  const storedDate = q?.scanDate && q.scanDate !== "TBD" ? q.scanDate : "";
  // The date a job books with: the first capture day, else the single date, unless it is
  // marked TBD (a date behind the switch is not a date the client was given), else the
  // first dated day of any kind. quoteCaptureDate is that rule, on the stored days as they
  // are (an old day with no type is read the same way booking reads it).
  const date = quoteCaptureDate(q);
  const range = multiDay
    ? getJobDateRange({ sessions: days, captureDate: storedDate })
    : { start: tbd ? "" : storedDate, end: tbd ? "" : storedDate };
  return {
    multiDay,
    tbd,
    // The stored single date (a legacy literal "TBD" reads as none).
    scanDate: storedDate,
    date,
    days,
    range,
    isStudio: !!q?.isStudio,
    location: q?.jobLocation || "",
  };
}

/** The Capture Date or Capture Schedule the page prints. */
export function scheduleLabel(q) {
  const s = readSchedule(q);
  if (!s.multiDay) return s.tbd ? "TBD" : (s.scanDate ? fmtDate(s.scanDate) : "TBD");
  const { start, end } = s.range;
  if (!start) return "TBD";
  return start === end ? fmtDate(start) : `${fmtDate(start)} – ${fmtDate(end)}`;
}

export const buildNewSession = (sessionType, date, { inheritLocation = "", inheritStudio = false, inheritAddress = null } = {}) => ({
  ...BLANK_SESSION(),
  sessionType,
  date: date || "",
  // A studio day carries the full structured studio address, so it reaches the job whole;
  // an on-location day carries the structured address it inherits (the quote's, or the
  // day before it) with the location text.
  ...(inheritStudio ? studioPatch() : { isStudio: false, location: inheritLocation || "", ...addressOf(inheritAddress) }),
});

// The structured address fields of a quote or a day (venue, address, city, state, zip).
const ADDRESS_FIELDS = ["locationVenue", "locationAddress", "locationCity", "locationState", "locationZip"];
const addressOf = (from) => Object.fromEntries(ADDRESS_FIELDS.map((k) => [k, from?.[k] || ""]));

// The job-level fields that follow the sessions list, so the printed date range, the
// dashboard and booking read the same schedule.
const fromSessions = (q, nextSessions) => {
  const sorted = sortSessions(nextSessions);
  const firstCapture = sorted.find((s) => s?.sessionType === DEFAULT_SESSION_TYPE && s?.date);
  const firstAny = sorted.find((s) => s?.date);
  const first = sorted[0] || null;
  return {
    sessions: sorted,
    scanDate: (firstCapture || firstAny)?.date || "",
    sections: applyTravelRule(sectionsOf(q), sorted),
    jobLocation: first ? (first.isStudio ? STUDIO_ADDRESS : (first.location || "")) : "",
    isStudio: first ? !!first.isStudio : false,
    // The structured address the job books with follows the first day too (BLANK_JOB
    // reads the quote-level fields), so a day's address never goes stale on the quote.
    ...addressOf(first ? (first.isStudio ? studioPatch() : first) : null),
  };
};

/* One change to the schedule, as the patch to spread over the quote. Exactly one key:
   - { scanDate }            single-day date
   - { tbd }                 single-day TBD switch
   - { studio }              single-day Studio switch (location and travel follow)
   - { location }            single-day location text
   - { multiDay }            the switch; off keeps the first day (the caller confirms)
   - { sessions }            the whole list (scanDate, location and travel follow)
   - { updateSession: { id, patch } }, { addSession: sessionType }, { removeSession: id },
     { toggleSessionStudio: id }
   Returns { patch } or { refuse: reason } when the change can't be made. */
export function writeSchedule(q, change) {
  const sessions = hydrateSessions(q);
  if ("scanDate" in change) return { patch: { scanDate: change.scanDate || "" } };
  if ("tbd" in change) return { patch: { scanDateTBD: !!change.tbd } };
  if ("location" in change) return { patch: { jobLocation: change.location || "" } };
  if ("studio" in change) {
    const checked = !!change.studio;
    return { patch: { isStudio: checked, jobLocation: checked ? STUDIO_ADDRESS : "", sections: applyTravelRule(sectionsOf(q), [{ isStudio: checked }]) } };
  }
  if ("multiDay" in change) {
    if (!change.multiDay) {
      const first = sortSessions(sessions)[0];
      const keptStudio = first ? !!first.isStudio : !!q?.isStudio;
      return { patch: {
        multiDay: false, sessions: [],
        scanDate: first?.date || q?.scanDate || "",
        // The kept day's date is the single date now: a date set while multi-day is not
        // TBD; with no date on the day, the TBD switch stays as it was.
        scanDateTBD: first?.date ? false : !!q?.scanDateTBD,
        jobLocation: keptStudio ? STUDIO_ADDRESS : (first?.location || q?.jobLocation || ""),
        isStudio: keptStudio,
        // The structured address the job books with follows the kept day too.
        ...addressOf(keptStudio ? studioPatch() : (first || q)),
        // The travel clause follows the kept day, not a discarded on-location one.
        sections: applyTravelRule(sectionsOf(q), first ? [first] : []),
      } };
    }
    if (q?.multiDay) return { patch: {} };
    // Days the quote already holds (a job's days pulled in by Amend, with their calendar
    // events and notes) are the schedule; otherwise one capture day is seeded from the
    // single date and the quote's location (structured address included). multiDay with
    // no sessions is an invalid state.
    const held = sortSessions(sessions);
    const days = held.length ? held : [buildNewSession(DEFAULT_SESSION_TYPE, q?.scanDate || "", { inheritLocation: q?.jobLocation, inheritStudio: q?.isStudio, inheritAddress: q })];
    return { patch: { multiDay: true, sessions: days, sections: applyTravelRule(sectionsOf(q), days) } };
  }
  if ("sessions" in change) return { patch: fromSessions(q, change.sessions) };
  if ("updateSession" in change) {
    const { id, patch } = change.updateSession;
    const next = sessions.map((s) => (s.id === id ? { ...s, ...patch } : s));
    if (patch.date || patch.sessionType) {
      const check = validateSessionAdd({ sessions: next }, next.find((s) => s.id === id));
      if (!check.ok) return { refuse: check.reason };
    }
    return { patch: fromSessions(q, next) };
  }
  if ("toggleSessionStudio" in change) {
    const id = change.toggleSessionStudio;
    const target = sessions.find((s) => s.id === id);
    if (!target) return { refuse: "That day is no longer on the quote." };
    return { patch: fromSessions(q, sessions.map((s) => (s.id === id ? { ...s, ...(s.isStudio ? clearLocationPatch() : studioPatch()) } : s))) };
  }
  if ("removeSession" in change) {
    // A multi-day quote keeps at least one day. Turning multi-day off removes the rest.
    if (sessions.length <= 1) return { refuse: "A multi-day quote needs at least one session. Turn off multi-day to remove it." };
    return { patch: fromSessions(q, sessions.filter((s) => s.id !== change.removeSession)) };
  }
  if ("addSession" in change) {
    const sorted = sortSessions(sessions);
    const inheritFrom = sorted[sorted.length - 1] || null;
    const inheritLocation = inheritFrom?.location || q?.jobLocation || "";
    const inheritStudio = inheritFrom ? !!inheritFrom.isStudio : !!q?.isStudio;
    const defaultDate = defaultDateFor(change.addSession, sorted) || inheritFrom?.date || q?.scanDate || "";
    const candidate = buildNewSession(change.addSession, defaultDate, { inheritLocation, inheritStudio, inheritAddress: inheritFrom || q });
    const check = validateSessionAdd({ sessions }, candidate);
    // A clashing default date (same date and type) is dropped so the operator picks one.
    return { patch: fromSessions(q, [...sessions, check.ok ? candidate : { ...candidate, date: "" }]) };
  }
  return { refuse: "Unknown schedule change." };
}
